import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Sync as SyncIcon,
  Visibility as ViewIcon,
  Rule as RuleIcon,
  MoreVert as MoreIcon,
  Warning as WarningIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { StatusChip } from '../../../../../components/shared';
import { useIsMobile } from '../../../../../hooks/useIsMobile';
import { ExportersTableProps, Exporter } from '../types';

/**
 * ExportersTable - Displays the list of monitored exporters.
 *
 * Features:
 * - Header with "Generate Installer" and "Add Exporter" buttons
 * - Table with columns: Name, Type, Host, Status, Alerts, Last Seen, Actions
 * - Clickable alert count opens the AlertsDrawer
 * - Quick action buttons: View, Manage Alerts, More Actions
 * - Helper tip at the bottom
 */
export function ExportersTable({
  exporters,
  onOpenAlertsDrawer,
  onExporterClick,
  onMenuOpen,
  onAddExporter,
  onGenerateInstaller,
  onSync,
  syncing,
}: ExportersTableProps) {
  const isMobile = useIsMobile();
  const theme = useTheme();

  /**
   * Calculate alert count based on status (simulated).
   * In production, this would come from actual alert data.
   */
  const getAlertCount = (exporter: Exporter): number => {
    if (exporter.status === 'down') return 2;
    if (exporter.status === 'warning') return 1;
    return 0;
  };

  return (
    <Box>
      {/* Header with Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Exporters
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            variant="outlined"
            size="small"
            onClick={onSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
            size="small"
            onClick={onGenerateInstaller}
          >
            Generate Installer
          </Button>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
            onClick={onAddExporter}
          >
            Add Exporter
          </Button>
        </Box>
      </Box>

      {/* Mobile Card View / Desktop Table View */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {exporters.map((exporter) => {
            const alertCount = getAlertCount(exporter);
            return (
              <Paper key={exporter.id} elevation={0} onClick={() => onExporterClick?.(exporter)} sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, cursor: onExporterClick ? 'pointer' : undefined, ...((!exporter.enabled) ? { opacity: 0.5 } : undefined) }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">{exporter.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">{exporter.name}</Typography>
                  </Box>
                  {!exporter.enabled ? (
                    <Chip label="Paused" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />
                  ) : (
                    <StatusChip status={exporter.status} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                  <Chip label={exporter.type} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary" fontFamily="monospace">{exporter.host}</Typography>
                  <Typography variant="caption" color="text.secondary">{exporter.lastSeen}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {alertCount > 0 ? (
                    <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label={`${alertCount} active`} size="small" color="error" variant="outlined" onClick={(e) => { e.stopPropagation(); onOpenAlertsDrawer(exporter); }} />
                  ) : (
                    <Chip label="No alerts" size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onExporterClick?.(exporter); }}><ViewIcon /></IconButton>
                    <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); onOpenAlertsDrawer(exporter); }}><RuleIcon /></IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenuOpen(e, 'exporter', exporter); }}><MoreIcon /></IconButton>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50' }}>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Alerts</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {exporters.map((exporter) => {
                const alertCount = getAlertCount(exporter);

                return (
                  <TableRow key={exporter.id} hover sx={{ ...(!exporter.enabled ? { opacity: 0.5 } : undefined), cursor: onExporterClick ? 'pointer' : undefined }} onClick={() => onExporterClick?.(exporter)}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {exporter.displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {exporter.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={exporter.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {exporter.host}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {!exporter.enabled ? (
                        <Chip label="Paused" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />
                      ) : (
                        <StatusChip status={exporter.status} />
                      )}
                    </TableCell>
                    <TableCell>
                      {alertCount > 0 ? (
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                          label={`${alertCount} active`}
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={(e) => { e.stopPropagation(); onOpenAlertsDrawer(exporter); }}
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <Chip
                          label="No alerts"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {exporter.lastSeen}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onExporterClick?.(exporter); }}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage Alerts & Subscribers">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => { e.stopPropagation(); onOpenAlertsDrawer(exporter); }}
                        >
                          <RuleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); onMenuOpen(e, 'exporter', exporter); }}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {exporters.length === 0 && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <ComputerIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No exporters configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a Prometheus exporter to start monitoring your infrastructure.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddExporter}>
            Add Your First Exporter
          </Button>
        </Box>
      )}

      {/* Helper Tip */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        Click the{' '}
        <RuleIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> icon to
        manage alert rules, or use the menu for more options
      </Typography>
    </Box>
  );
}

export default ExportersTable;
